/**
 * entranceMarks.ts — door-as-width-rectangle entrance marking (Slice 3 Stage 3b).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS EXISTS (grid-first sequencing + owner door-rectangle correction)
 * ─────────────────────────────────────────────────────────────────────────
 *  Slice 2 asked staff to TYPE entrance pada codes blind — corrected to
 *  click-on-grid (pada is an OUTPUT of the grid, memory:
 *  project_avbp_grid_first_sequencing). Stage 3 first modelled the entrance as
 *  a single POINT → one pada. The owner rejected THAT too: a real door has
 *  WIDTH, sits ON the wall as a RECTANGLE, and can span MULTIPLE padas.
 *
 *  STAGE 3b model (recovered Sprint-A.2, `doorGeometry.ts`): a door snaps to the
 *  nearest boundary EDGE and is parameterised by `edge_index` + `parametric_t`
 *  (centre slides along the wall) + `width_image_px` (the opening width). The
 *  pada is no longer one code but the SET of padas the door's width overlaps —
 *  OWNER RULE (LOCKED): a door counts in EVERY pada its width covers, one
 *  entrance finding per covered pada.
 *
 *  A DOOR MARK carries:
 *   · `edge_index` + `parametric_t` — where the door sits on the boundary.
 *   · `width_image_px` — the opening width (image-pixel space, OQ-C).
 *   · `centre {x,y}` — derived (edge_index,t → point), for rendering.
 *   · `padas string[]` — derived: every covered pada, in order (e.g. ['N3','N4']).
 *     This is the trust signal that replaces blind typing.
 *   · `type` — one of the 5-type vocabulary (memory: reference_entrance_vocabulary
 *     — windows EXCLUDED, balcony openings INCLUDED).
 *   · `name?` — optional free label.
 *   · `isPrincipal` — exactly ONE mark per floor is the principal/main entry;
 *     its FIRST covered pada becomes `floor.main_entry_label`.
 *
 *  PERSIST CONTRACT (verified against the engine read site):
 *   - rich UI markers → `answers.floors[i].doors[] = { edge_index, parametric_t,
 *       width_image_px, type, name?, principal }` (re-render on reload).
 *   - DERIVED engine-facing → `answers.floors[i].entrances[]` = one
 *       `{label:<pada>}` per covered pada per door (flatMap), `main_entry_label`
 *       = the principal door's primary pada.
 *   The composer reads each entrance's `.label` and (fallback) `.main_entry_label`
 *   at `functions/src/composer/pipeline/step1_entrance.js:39-40`, emitting ONE
 *   finding per `entrances[]` element. So a door covering N3+N4 persists as
 *   `[{label:'N3'},{label:'N4'}]` → two findings. That is the owner's rule.
 *
 *  This module is PURE (no Firebase, no DOM) so it unit-tests via _tsload.
 *  The door→padas computation reuses `doorGeometry.ts`, which in turn reuses the
 *  ported grid math in `vastuGrid.ts` — ONE copy of the geometry; no second
 *  classifier.
 * ─────────────────────────────────────────────────────────────────────────
 */
import {
  padasForInteriorSegment,
  pointInPolygon,
  type GridResult,
  type Pt,
} from './vastuGrid';
import {
  cellsForSegment,
  cellsFromRecord,
  doorFromEnds,
  edgePointAt,
  nearestEdge,
  perimeterPos,
  pointOnEdge,
  walkPerimeter,
} from './doorGeometry';
import {
  ENTRANCE_TYPES,
  PERIMETER_ENTRANCE_TYPES,
  INTERNAL_ENTRANCE_TYPES,
  isEntranceCode,
  isEntranceType,
  isInternalEntranceType,
  newId,
  type DoorInput,
  type DoorPlacement,
  type EntranceInput,
  type EntranceType,
  type FloorInput,
} from './staffIntake';

/** Re-export so the UI imports the vocab from one place. */
export { ENTRANCE_TYPES, PERIMETER_ENTRANCE_TYPES, INTERNAL_ENTRANCE_TYPES };
export type { EntranceType };

/**
 * Default door opening width in IMAGE PIXELS. A single named constant so the
 * owner can later dictate a different default in one place. ~7% of a typical
 * 800px-wide plan ≈ a real single-leaf door at plan scale.
 */
export const DEFAULT_DOOR_WIDTH_PX = 56;

/** Min door width so a door can't be shrunk to a zero-width line. */
export const MIN_DOOR_WIDTH_PX = 8;

/**
 * One door marker, as the FORM holds it (pre-persist). Two placements share the
 * SAME rectangular width-door representation (two endpoints + width); the only
 * difference is whether the door rides a WALL or floats inside the plot:
 *
 *  · PERIMETER — a width-rectangle SNAPPED to the wall (`edge_index` +
 *    `parametric_t` slide the centre along the boundary; `width_image_px` sets
 *    the span). Covered `padas` = every pada the width spans along the boundary.
 *  · INTERNAL — the SAME rectangular width-door placed FREELY inside the plot: a
 *    SEGMENT with two free endpoints `a`/`b` (set its orientation + length, NOT
 *    snapped to a wall) + `width_image_px` (band thickness). Covered `padas` =
 *    every pada its angular span subtends from the centroid (by DIRECTION).
 *
 * In BOTH cases `padas` is the trust signal (never typed) AND can be MULTIPLE —
 * a perimeter door spans padas by boundary position, an internal door by
 * direction. `a`/`b` are null for a perimeter door; `edge_index`/`parametric_t`
 * are 0 for an internal door.
 */
export interface EntranceMark {
  /** form-local id (marker key + remove/drag handle). */
  id: string;
  /** 'perimeter' (on the wall) | 'internal' (free inside the plot). */
  placement: DoorPlacement;
  /** which boundary edge the door snaps to (PERIMETER). 0 for internal. */
  edge_index: number;
  /** centre position along that edge, 0..1 (PERIMETER). 0 for internal. */
  parametric_t: number;
  /** door opening width in image pixels (BOTH — band thickness / span). */
  width_image_px: number;
  /** first free endpoint of the internal door segment (INTERNAL). null for perimeter. */
  a: Pt | null;
  /** second free endpoint of the internal door segment (INTERNAL). null for perimeter. */
  b: Pt | null;
  /** derived centre point (image px), for rendering. INTERNAL = midpoint of a–b. */
  centre: Pt;
  /** derived padas. PERIMETER: every pada the width spans (e.g. ['N3','N4']).
   *  INTERNAL: every pada the angular span subtends (e.g. ['E4','E5']). [] off-grid. */
  padas: string[];
  /** type vocab (perimeter or internal set, per placement). */
  type: EntranceType;
  /** optional free name, e.g. "Front door". */
  name?: string;
  /** exactly one mark per floor is the principal/main entry. */
  isPrincipal: boolean;
}

/* ───────────────────────── door → covered padas ───────────────────────── */

/** Clamp a width to [MIN, +∞). */
export function clampWidth(w: number): number {
  return Number.isFinite(w) ? Math.max(MIN_DOOR_WIDTH_PX, w) : DEFAULT_DOOR_WIDTH_PX;
}

/**
 * PURE — the covered padas for a door (edge_index, t, width) against the grid.
 * Reuses the reusable `cellsForSegment` over the grid's own `entrancePolys`
 * (the 32 padas) — on-screen pada == report pada. Returns [] when there is no
 * grid / boundary, or the door fell outside every pada.
 */
export function padasForDoor(
  grid: GridResult | null,
  boundary: Pt[] | null | undefined,
  edgeIndex: number,
  parametricT: number,
  widthImagePx: number,
): string[] {
  if (!grid || !Array.isArray(boundary) || boundary.length < 2) return [];
  const cells = cellsFromRecord(grid.entrancePolys);
  return cellsForSegment(
    boundary,
    edgeIndex,
    parametricT,
    clampWidth(widthImagePx),
    cells,
  );
}

/** PURE — derive a door's centre point from edge_index + t. */
function centreOf(
  boundary: Pt[] | null | undefined,
  edgeIndex: number,
  parametricT: number,
): Pt {
  if (!Array.isArray(boundary) || boundary.length < 2) return { x: 0, y: 0 };
  const p = edgePointAt(boundary, edgeIndex, parametricT);
  return p ? { x: Math.round(p.x), y: Math.round(p.y) } : { x: 0, y: 0 };
}

/* ───────────────────────── internal door → direction padas ───────────────────────── */

/** Midpoint of an internal door's two endpoints (its render centre). */
function midOf(a: Pt, b: Pt): Pt {
  return { x: Math.round((a.x + b.x) / 2), y: Math.round((a.y + b.y) / 2) };
}

/** A finite {x,y} or null (defensive read of a persisted/optional point). */
function finitePt(p: Pt | null | undefined): Pt | null {
  return p &&
    typeof p.x === 'number' &&
    typeof p.y === 'number' &&
    Number.isFinite(p.x) &&
    Number.isFinite(p.y)
    ? { x: p.x, y: p.y }
    : null;
}

/**
 * PURE — the covered padas for an INTERNAL door SEGMENT (free endpoints a→b),
 * read by DIRECTION — every 32-radial sector its angular span subtends from the
 * centroid (owner ruling). Returns `[]` when there is no grid or the endpoints
 * are degenerate. Wraps `padasForInteriorSegment` so the UI / persist layers
 * share ONE read (identical to how a perimeter door reuses `cellsForSegment`).
 */
export function padasForInternalSegment(
  grid: GridResult | null,
  a: Pt | null | undefined,
  b: Pt | null | undefined,
): string[] {
  return padasForInteriorSegment(grid, a, b);
}

/** PURE — is an interior point inside the traced boundary? (placement guard.) */
export function isInsideBoundary(boundary: Pt[] | null | undefined, pt: Pt): boolean {
  return Array.isArray(boundary) && boundary.length >= 3 && pointInPolygon(pt, boundary);
}

/* ───────────────────────── mark builders / ops ───────────────────────── */

/** A sensible default PERIMETER type: the first door on a floor is the main door. */
function defaultType(existing: EntranceMark[]): EntranceType {
  return existing.length === 0 ? 'main_door' : 'secondary_door';
}

/**
 * PURE — build a fresh PERIMETER door for a CLICK point: snap to the nearest
 * boundary edge, drop a door of the default width centred there, derive its
 * centre + covered padas off `grid`. The first mark on an empty floor becomes
 * the principal automatically; the staff can re-designate.
 */
export function makeMark(
  grid: GridResult | null,
  boundary: Pt[] | null | undefined,
  click: Pt,
  existing: EntranceMark[],
): EntranceMark | null {
  if (!Array.isArray(boundary) || boundary.length < 2) return null;
  const hit = nearestEdge(click, boundary);
  if (!hit) return null;
  const width = DEFAULT_DOOR_WIDTH_PX;
  return {
    id: newId('en'),
    placement: 'perimeter',
    edge_index: hit.edge_index,
    parametric_t: hit.parametric_t,
    width_image_px: width,
    a: null,
    b: null,
    centre: centreOf(boundary, hit.edge_index, hit.parametric_t),
    padas: padasForDoor(grid, boundary, hit.edge_index, hit.parametric_t, width),
    type: defaultType(existing),
    isPrincipal: existing.length === 0,
  };
}

/** PURE — append a new PERIMETER door for a click (the first one = principal). */
export function addMark(
  marks: EntranceMark[],
  grid: GridResult | null,
  boundary: Pt[] | null | undefined,
  click: Pt,
): EntranceMark[] {
  const m = makeMark(grid, boundary, click, marks);
  return m ? [...marks, m] : marks;
}

/**
 * PURE — build a fresh INTERNAL door at a free interior CLICK point. The SAME
 * rectangular width-door as a perimeter door, just NOT snapped to a wall: it is
 * a horizontal SEGMENT of default width centred on the click, with two free
 * end-grips the staff can drag to rotate / resize. Its covered padas are read by
 * DIRECTION (every sector its angular span subtends). Default type =
 * 'staircase_entry' (apartment entry off a staircase). The first mark on a floor
 * becomes the principal. Returns null if there is no grid (no pada read).
 */
export function makeInternalMark(
  grid: GridResult | null,
  click: Pt,
  existing: EntranceMark[],
): EntranceMark | null {
  if (!grid) return null;
  const cx = Math.round(click.x);
  const cy = Math.round(click.y);
  const half = DEFAULT_DOOR_WIDTH_PX / 2;
  const a = { x: cx - half, y: cy };
  const b = { x: cx + half, y: cy };
  return {
    id: newId('in'),
    placement: 'internal',
    edge_index: 0,
    parametric_t: 0,
    width_image_px: DEFAULT_DOOR_WIDTH_PX,
    a,
    b,
    centre: midOf(a, b),
    padas: padasForInternalSegment(grid, a, b),
    type: 'staircase_entry',
    isPrincipal: existing.length === 0,
  };
}

/** PURE — append a new INTERNAL door for a free interior click. */
export function addInternalMark(
  marks: EntranceMark[],
  grid: GridResult | null,
  click: Pt,
): EntranceMark[] {
  const m = makeInternalMark(grid, click, marks);
  return m ? [...marks, m] : marks;
}

/**
 * PURE — TRANSLATE an INTERNAL door so its CENTRE follows the drag point (the
 * body-drag), keeping its orientation + length, then re-read its covered padas
 * by direction. (A perimeter door uses `moveMark`, which slides along its wall;
 * an internal door moves freely and keeps its shape.)
 */
export function moveInternalMark(
  marks: EntranceMark[],
  id: string,
  drag: Pt,
  grid: GridResult | null,
): EntranceMark[] {
  return marks.map((m) => {
    if (m.id !== id || m.placement !== 'internal' || !m.a || !m.b) return m;
    const mid = m.centre;
    const dx = Math.round(drag.x) - mid.x;
    const dy = Math.round(drag.y) - mid.y;
    const a = { x: m.a.x + dx, y: m.a.y + dy };
    const b = { x: m.b.x + dx, y: m.b.y + dy };
    return { ...m, a, b, centre: midOf(a, b), padas: padasForInternalSegment(grid, a, b) };
  });
}

/**
 * PURE — drag ONE END of an INTERNAL door to a free interior point (the two
 * end-grips). The OTHER end stays anchored; the door rotates + resizes about it.
 * Re-reads covered padas by direction. `which` = 'start' (endpoint a) / 'end'
 * (endpoint b). This is the internal analogue of `setMarkEnd` (perimeter), but
 * the moved end goes wherever it is dropped — NOT snapped to a wall.
 */
export function setInternalEnd(
  marks: EntranceMark[],
  id: string,
  which: 'start' | 'end',
  drag: Pt,
  grid: GridResult | null,
): EntranceMark[] {
  const pt = { x: Math.round(drag.x), y: Math.round(drag.y) };
  return marks.map((m) => {
    if (m.id !== id || m.placement !== 'internal' || !m.a || !m.b) return m;
    const a = which === 'start' ? pt : m.a;
    const b = which === 'end' ? pt : m.b;
    const width = Math.max(MIN_DOOR_WIDTH_PX, Math.hypot(b.x - a.x, b.y - a.y));
    return {
      ...m,
      a,
      b,
      width_image_px: width,
      centre: midOf(a, b),
      padas: padasForInternalSegment(grid, a, b),
    };
  });
}

/** PURE — remove a door by id. If the principal was removed, promote the first
 *  remaining (a floor with ≥1 door always has a principal). */
export function removeMark(marks: EntranceMark[], id: string): EntranceMark[] {
  const next = marks.filter((m) => m.id !== id);
  if (next.length && !next.some((m) => m.isPrincipal)) {
    next[0] = { ...next[0], isPrincipal: true };
  }
  return next;
}

/**
 * PURE — SLIDE a door to a new drag point: re-project onto its OWN edge
 * (pointOnEdge keeps the door on the same wall as it slides), then re-derive
 * centre + covered padas. Used by the body drag.
 */
export function moveMark(
  marks: EntranceMark[],
  id: string,
  drag: Pt,
  grid: GridResult | null,
  boundary: Pt[] | null | undefined,
): EntranceMark[] {
  if (!Array.isArray(boundary) || boundary.length < 2) return marks;
  return marks.map((m) => {
    if (m.id !== id) return m;
    // an internal door slides freely (use moveInternalMark) — not along a wall.
    if (m.placement === 'internal') return m;
    const proj = pointOnEdge(drag, boundary, m.edge_index);
    if (!proj) return m;
    return {
      ...m,
      parametric_t: proj.parametric_t,
      centre: centreOf(boundary, m.edge_index, proj.parametric_t),
      padas: padasForDoor(grid, boundary, m.edge_index, proj.parametric_t, m.width_image_px),
    };
  });
}

/**
 * PURE — set a door's WIDTH (the width handle) and re-derive covered padas.
 * Clamps to the min width.
 */
export function setMarkWidth(
  marks: EntranceMark[],
  id: string,
  width: number,
  grid: GridResult | null,
  boundary: Pt[] | null | undefined,
): EntranceMark[] {
  const w = clampWidth(width);
  return marks.map((m) =>
    m.id === id && m.placement !== 'internal' // internal doors have no width
      ? {
          ...m,
          width_image_px: w,
          padas: padasForDoor(grid, boundary, m.edge_index, m.parametric_t, w),
        }
      : m,
  );
}

/**
 * PURE — drag ONE END of a door along the wall (item 4 — on-drawing end grips).
 * The OTHER end stays anchored; the door's centre + width are rebuilt from the
 * (anchor, moved-end) pair via the perimeter arc-length math, so the owner sets
 * the exact span visually. `which` = 'start' (the centre−width/2 end) or 'end'
 * (the centre+width/2 end). The drag point is snapped to the nearest boundary
 * edge first, then converted to a perimeter position. Re-derives covered padas.
 */
export function setMarkEnd(
  marks: EntranceMark[],
  id: string,
  which: 'start' | 'end',
  drag: Pt,
  grid: GridResult | null,
  boundary: Pt[] | null | undefined,
): EntranceMark[] {
  if (!Array.isArray(boundary) || boundary.length < 2) return marks;
  return marks.map((m) => {
    if (m.id !== id) return m;
    if (m.placement === 'internal') return m; // no end-grips on an internal door
    const half = clampWidth(m.width_image_px) / 2;
    // The end that stays put (the opposite of the one being dragged).
    const anchorPt =
      which === 'start'
        ? walkPerimeter(boundary, m.edge_index, m.parametric_t, half, 1)
        : walkPerimeter(boundary, m.edge_index, m.parametric_t, half, -1);
    // Snap the drag to ANY edge (the grip can cross corners) → its perimeter pos.
    const hit = nearestEdge(drag, boundary);
    if (!hit) return m;
    const newEndPos = perimeterPos(boundary, hit.edge_index, hit.parametric_t);
    const anchorHit = nearestEdge(anchorPt, boundary);
    if (!anchorHit) return m;
    const anchorPos = perimeterPos(
      boundary,
      anchorHit.edge_index,
      anchorHit.parametric_t,
    );
    const rebuilt = doorFromEnds(boundary, anchorPos, newEndPos);
    const width = clampWidth(rebuilt.width_image_px);
    return {
      ...m,
      edge_index: rebuilt.edge_index,
      parametric_t: rebuilt.parametric_t,
      width_image_px: width,
      centre: centreOf(boundary, rebuilt.edge_index, rebuilt.parametric_t),
      padas: padasForDoor(grid, boundary, rebuilt.edge_index, rebuilt.parametric_t, width),
    };
  });
}

/**
 * PURE — set a door's type, ENFORCING the placement's vocab: a perimeter door
 * accepts only the 5 perimeter types, an internal door only the internal types.
 * An out-of-vocab type for the door's placement is ignored.
 */
export function setMarkType(
  marks: EntranceMark[],
  id: string,
  type: string,
): EntranceMark[] {
  if (!isEntranceType(type)) return marks;
  return marks.map((m) => {
    if (m.id !== id) return m;
    const ok =
      m.placement === 'internal'
        ? isInternalEntranceType(type)
        : !isInternalEntranceType(type); // perimeter ⇒ one of the 5 perimeter types
    return ok ? { ...m, type } : m;
  });
}

/** PURE — set a door's optional name. */
export function setMarkName(
  marks: EntranceMark[],
  id: string,
  name: string,
): EntranceMark[] {
  return marks.map((m) => (m.id === id ? { ...m, name } : m));
}

/**
 * PURE — designate exactly ONE door as the principal/main entry. A no-op id
 * leaves the list unchanged (never produces zero principals on a non-empty floor).
 */
export function setPrincipal(marks: EntranceMark[], id: string): EntranceMark[] {
  if (!marks.some((m) => m.id === id)) return marks;
  return marks.map((m) => ({ ...m, isPrincipal: m.id === id }));
}

/**
 * PURE — re-derive EVERY door's centre + covered padas off a (new) grid/boundary.
 * Called when the boundary or north changes — the grid moved, so each door's
 * pada-coverage must be recomputed from its (unchanged) edge_index/t/width. A
 * door now off the boundary gets `padas: []`.
 */
export function rederiveMarks(
  marks: EntranceMark[],
  grid: GridResult | null,
  boundary: Pt[] | null | undefined,
): EntranceMark[] {
  return marks.map((m) => {
    if (m.placement === 'internal') {
      // internal door: the segment (a,b) is fixed in image space; only its
      // direction-padas change when north rotates the grid.
      const a = m.a;
      const b = m.b;
      if (a && b) {
        return { ...m, centre: midOf(a, b), padas: padasForInternalSegment(grid, a, b) };
      }
      return { ...m, padas: padasForInternalSegment(grid, a, b) };
    }
    return {
      ...m,
      centre: centreOf(boundary, m.edge_index, m.parametric_t),
      padas: padasForDoor(grid, boundary, m.edge_index, m.parametric_t, m.width_image_px),
    };
  });
}

/* ───────────────────────── persist mapping ───────────────────────── */

/** PURE — one mark → its rich `doors[]` persisted entry (re-renders on reload). */
export function markToDoorInput(m: EntranceMark): DoorInput {
  const out: DoorInput =
    m.placement === 'internal'
      ? {
          placement: 'internal',
          a: { x: Math.round((m.a ?? m.centre).x), y: Math.round((m.a ?? m.centre).y) },
          b: { x: Math.round((m.b ?? m.centre).x), y: Math.round((m.b ?? m.centre).y) },
          width_image_px: +clampWidth(m.width_image_px).toFixed(2),
          principal: !!m.isPrincipal,
        }
      : {
          placement: 'perimeter',
          edge_index: m.edge_index,
          parametric_t: +m.parametric_t.toFixed(4),
          width_image_px: +clampWidth(m.width_image_px).toFixed(2),
          principal: !!m.isPrincipal,
        };
  if (m.type) out.type = m.type;
  const name = (m.name || '').trim();
  if (name) out.name = name;
  return out;
}

/**
 * PURE — the marks of one floor → the floor's persist patch:
 *   · `doors[]`     — the rich UI markers (one per door).
 *   · `entrances[]` — DERIVED: one `{label:<pada>}` per COVERED pada per door
 *     (flatMap). This is what the engine reads; a door over N3+N4 yields TWO
 *     entries → two findings (owner rule). De-duplicated WITHIN one floor so two
 *     doors covering the same pada don't double-emit it.
 *   · `main_entry_label` — the principal door's PRIMARY (first covered) pada;
 *     falls back to the first valid pada on the floor.
 * A floor with no placeable doors yields empty doors/entrances + '' main label.
 */
export function marksToFloorPatch(marks: EntranceMark[]): {
  doors: DoorInput[];
  entrances: EntranceInput[];
  main_entry_label: string;
} {
  const doors = marks.map(markToDoorInput);

  // entrances = flatMap door → its covered padas, de-duplicated across the floor.
  const seen = new Set<string>();
  const entrances: EntranceInput[] = [];
  for (const m of marks) {
    for (const raw of m.padas) {
      const code = (raw || '').trim().toUpperCase();
      if (!isEntranceCode(code) || seen.has(code)) continue;
      seen.add(code);
      const e: EntranceInput = { label: code };
      if (m.type) e.type = m.type;
      const name = (m.name || '').trim();
      if (name) e.name = name;
      entrances.push(e);
    }
  }

  // main label = the principal door's first covered pada (else first valid pada).
  const principal = marks.find(
    (m) => m.isPrincipal && m.padas.some((p) => isEntranceCode((p || '').toUpperCase())),
  );
  const fallback = marks.find((m) =>
    m.padas.some((p) => isEntranceCode((p || '').toUpperCase())),
  );
  const main =
    (principal || fallback)?.padas.find((p) =>
      isEntranceCode((p || '').toUpperCase()),
    ) || '';
  return {
    doors,
    entrances,
    main_entry_label: isEntranceCode(main.toUpperCase()) ? main.toUpperCase() : '',
  };
}

/**
 * PURE — hydrate the door marks for one floor from its PERSISTED shape. Reads
 * `floor.doors[]` (stored by Stage 3b) and RE-DERIVES centre + covered padas off
 * the supplied grid/boundary (so the on-map render always reflects the current
 * grid). The principal is the door flagged `principal` (else the first).
 *
 * A legacy floor saved BEFORE Stage 3b has NO `doors[]` (only typed/point
 * entrances) — those produce no door markers here (not renderable as a wall
 * rectangle), but they are NOT destroyed: `floorToAnswer` round-trips the
 * `entrances[]` until the staff re-marks the floor with doors.
 */
export function marksFromFloor(
  floor: FloorInput,
  grid: GridResult | null,
  boundary: Pt[] | null | undefined,
): EntranceMark[] {
  const src = floor.doors || [];
  let principalAssigned = false;
  const out: EntranceMark[] = [];
  for (let i = 0; i < src.length; i++) {
    const d = src[i];
    const internal = d.placement === 'internal';
    // STABLE id = the door's index in the persisted array, so a mark's id is the
    // SAME across re-derivations within a render cycle (the panel re-derives
    // marks on every mutation). A random id would break drag/remove which
    // reference an id from the previously-rendered list.
    const id = `en${i}`;
    const isP = !principalAssigned && d.principal === true;

    if (internal) {
      // Prefer the segment (a,b); migrate a legacy point `at` to a tiny segment
      // so an old internal door renders as a rectangle.
      let a = finitePt(d.a);
      let b = finitePt(d.b);
      if (!a || !b) {
        const at = finitePt(d.at);
        if (!at) continue;
        const half = DEFAULT_DOOR_WIDTH_PX / 2;
        a = { x: at.x - half, y: at.y };
        b = { x: at.x + half, y: at.y };
      }
      const width = clampWidth(
        typeof d.width_image_px === 'number' ? d.width_image_px : DEFAULT_DOOR_WIDTH_PX,
      );
      if (isP) principalAssigned = true;
      out.push({
        id,
        placement: 'internal',
        edge_index: 0,
        parametric_t: 0,
        width_image_px: width,
        a,
        b,
        centre: midOf(a, b),
        padas: padasForInternalSegment(grid, a, b),
        // an internal door must carry an internal type; default to staircase_entry.
        type: isInternalEntranceType(d.type) ? d.type : 'staircase_entry',
        name: typeof d.name === 'string' ? d.name : undefined,
        isPrincipal: isP,
      });
      continue;
    }

    if (
      typeof d.edge_index !== 'number' ||
      !Number.isFinite(d.edge_index) ||
      typeof d.parametric_t !== 'number' ||
      !Number.isFinite(d.parametric_t)
    ) {
      continue;
    }
    const width = clampWidth(
      typeof d.width_image_px === 'number' ? d.width_image_px : DEFAULT_DOOR_WIDTH_PX,
    );
    if (isP) principalAssigned = true;
    out.push({
      id,
      placement: 'perimeter',
      edge_index: d.edge_index,
      parametric_t: d.parametric_t,
      width_image_px: width,
      a: null,
      b: null,
      centre: centreOf(boundary, d.edge_index, d.parametric_t),
      padas: padasForDoor(grid, boundary, d.edge_index, d.parametric_t, width),
      // a perimeter door must carry a perimeter type; default secondary_door.
      type:
        isEntranceType(d.type) && !isInternalEntranceType(d.type)
          ? d.type
          : 'secondary_door',
      name: typeof d.name === 'string' ? d.name : undefined,
      isPrincipal: isP,
    });
  }
  // If none was flagged principal but there ARE doors, principal the first.
  if (out.length && !out.some((m) => m.isPrincipal)) {
    out[0] = { ...out[0], isPrincipal: true };
  }
  return out;
}
